/**
 * Utility functions for safely calling tracking functions
 * These functions check if the tracking function exists before calling it
 * and retry if the function is not available yet
 */

// Retry configuration
const RETRY_CONFIG = {
  maxAttempts: 10,
  delayMs: 100,
  maxDelayMs: 1000,
}

// Cache for tracking function availability to avoid repeated checks
const functionCache = new Map()

// Helper function to delay execution
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms))

// Helper function to retry tracking calls
const retryTrackingCall = async (functionName, args, attempt = 1) => {
  if (attempt > RETRY_CONFIG.maxAttempts) {
    console.warn(`Tracking function ${functionName} not available after ${RETRY_CONFIG.maxAttempts} attempts`)
    return
  }

  // Check cache first for better performance
  if (functionCache.has(functionName)) {
    const cachedFunction = functionCache.get(functionName)
    if (cachedFunction) {
      cachedFunction(...args)
      return
    }
  }

  if (window[functionName]) {
    // Cache the function for future calls
    functionCache.set(functionName, window[functionName])
    window[functionName](...args)
    return
  }

  // Function not available, retry after delay
  const delayMs = Math.min(RETRY_CONFIG.delayMs * attempt, RETRY_CONFIG.maxDelayMs)
  await delay(delayMs)

  // Retry
  retryTrackingCall(functionName, args, attempt + 1)
}

// Pre-define the retry calls to avoid creating new functions on each call
const createRetryCall = (functionName) => (args) =>
  retryTrackingCall(functionName, args).catch(() => {})

const retrySelectItem = createRetryCall('trackSelectItem')
const retryAddToCart = createRetryCall('trackAddToCart')
const retryRemoveFromCart = createRetryCall('trackRemoveFromCart')
const retryAddToWishList = createRetryCall('trackAddToWishList')
const retrySelectContent = createRetryCall('trackSelectContent')
const retrySuggestionClick = createRetryCall('trackSuggestionClick')
const retryUserSettings = createRetryCall('trackingUserSettings')

export const safeTrackSelectItem = (itemAttributes) => {
  if (window.trackSelectItem) {
    window.trackSelectItem(itemAttributes)
  } else {
    retrySelectItem([itemAttributes])
  }
}

export const safeTrackAddToCart = (itemAttributes, eventSource) => {
  if (window.trackAddToCart) {
    window.trackAddToCart(itemAttributes, eventSource)
  } else {
    retryAddToCart([itemAttributes, eventSource])
  }
}

export const safeTrackRemoveFromCart = (itemAttributes) => {
  if (window.trackRemoveFromCart) {
    window.trackRemoveFromCart(itemAttributes)
  } else {
    retryRemoveFromCart([itemAttributes])
  }
}

export const safeTrackAddToWishList = (itemAttributes, eventSource) => {
  if (window.trackAddToWishList) {
    window.trackAddToWishList(itemAttributes, eventSource)
  } else {
    retryAddToWishList([itemAttributes, eventSource])
  }
}

export const safeTrackSelectContent = (itemAttributes) => {
  if (window.trackSelectContent) {
    window.trackSelectContent(itemAttributes)
  } else {
    retrySelectContent([itemAttributes])
  }
}

export const safeTrackSuggestionClick = (props) => {
  if (window.trackSuggestionClick) {
    window.trackSuggestionClick(props)
  } else {
    retrySuggestionClick([props])
  }
}

export const safeTrackingUserSettings = (props) => {
  if (window.trackingUserSettings) {
    window.trackingUserSettings(props)
  } else {
    retryUserSettings([props])
  }
}

// Expose safe tracking functions globally for legacy JavaScript files
if (typeof window !== 'undefined') {
  window.safeTrackSelectItem = safeTrackSelectItem
  window.safeTrackAddToCart = safeTrackAddToCart
  window.safeTrackRemoveFromCart = safeTrackRemoveFromCart
  window.safeTrackAddToWishList = safeTrackAddToWishList
  window.safeTrackSelectContent = safeTrackSelectContent
  window.safeTrackSuggestionClick = safeTrackSuggestionClick
  window.safeTrackingUserSettings = safeTrackingUserSettings
}
